import { Fragment, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Tab, Tabs } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import { Formik } from 'formik';
import {
  AppFileUploader,
  AppInput,
  AppRemixIcon,
  AppTextArea,
  AppNotificationAlert,
} from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import colors from '../../../../constants/colors';
import Translator from '../../../components/helpers/Translator';
import plantObligationStates from '../../../../constants/plantObligationStates';
import RemixIcon from '../../../components/helpers/RemixIcon';
import AppSpinner from '../../../components/AppSpinner';
import NoInfo from '../../../components/helpers/NoInfo';
import AppModal from '../../../components/Modal/AppModal';
import { toFormData } from '../../../../minions/forms';
import images from '../../../../constants/images';
import InfoModal from '../../../components/Modal/InfoModal';
import { axiosPath } from '../../../../routing/helpers/router';
import errorHandler from '@/layout/components/error/ErrorHandler';
import _ from 'lodash';

const SingleLaw = (props) => {
  const {
    law,
    lawsDetails,
    evaluation,
    evidences,
    articleObligations,
    moduleBridgeHex,
    update,
  } = props;
  const dispatch = useDispatch();
  const global = useSelector((state) => state.global);
  const permissionsModule = useSelector(
    (state) => state.global?.permissions?.lawFinder,
  );
  const permissions = useSelector((state) => state.auth.permissions.client);
  const [showChangeStateModal, setShowChangeStateModal] = useState(false);
  const [showNewEvidenceModal, setShowNewEvidenceModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [lawDetail, setLawDetail] = useState();
  const [evaluationIsLoading, setEvaluationIsLoading] = useState(false);
  const [showDeleteEvidenceModal, setShowDeleteEvidenceModal] = useState(null);
  const [removeEvidenceIsLoading, setRemoveEvidenceIsLoading] = useState(false);
  const [articleData, setArticleData] = useState(null);
  const [checkedArticles, setCheckedArticles] = useState(null);
  const [maxIcons, setMaxIcons] = useState(7);

  const exportLawDocument = useCallback(
    async (lawID) => {
      await axios
        .post(axiosPath('api_r_user_module_lawFinder_exportLaw'), {
          lawID,
          moduleBridgeHex,
        })
        .then(({ data }) => {
          window.location = data.reportUrl;
        })
        .catch((err) => {
          dispatch(errorHandler(err));
        });
    },
    [moduleBridgeHex],
  );

  useEffect(() => {
    if (lawsDetails)
      setLawDetail(_.find(lawsDetails, (ld) => ld.id === law.id));
  }, [lawsDetails]);

  useEffect(() => {
    if (law.externalWebLink.length > 0 && law.lawFiles.length >= 7)
      setMaxIcons(6);
  }, [lawsDetails]);

  const handleChangeState = async (state) => {
    setEvaluationIsLoading(true);
    setShowChangeStateModal(false);
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_lawFinder_changeLawState'),
        { lawId: law.id, state, moduleBridgeHex },
      );
      setLawDetail(response.data);
      setEvaluationIsLoading(false);
      return response.data;
    } catch (err) {
      setEvaluationIsLoading(false);
    }
  };

  const handleAddEvidence = async (values) => {
    setEvaluationIsLoading(true);
    setShowChangeStateModal(false);
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_lawFinder_addLawEvidence'),
        toFormData({
          ...values,
          lawId: law.id,
          moduleBridgeHex,
        }),
      );
      setLawDetail(response.data);
      setEvaluationIsLoading(false);
      setShowNewEvidenceModal(false);
      return response.data;
    } catch (err) {
      setEvaluationIsLoading(false);
      setShowNewEvidenceModal(false);
      AppNotificationAlert({
        title: <Translator file="user" name="not.authorized.access" />,
        message: <Translator file="user" name="access.not.allowed" />,
        type: 'danger',
      });
      dispatch(errorHandler(err));
    }
  };

  const handleDeleteEvidence = async () => {
    setRemoveEvidenceIsLoading(true);
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_lawFinder_removeLawEvidence'),
        {
          lawEvidenceId: showDeleteEvidenceModal,
          lawId: law.id,
          moduleBridgeHex,
        },
      );
      setLawDetail(response.data);
      setRemoveEvidenceIsLoading(false);
      setShowDeleteEvidenceModal(null);
      return response.data;
    } catch (err) {
      setRemoveEvidenceIsLoading(false);
      setShowDeleteEvidenceModal(null);
      AppNotificationAlert({
        title: <Translator file="user" name="not.authorized.access" />,
        message: <Translator file="user" name="access.not.allowed" />,
        type: 'danger',
        duration: 5000,
      });
    }
  };
  const handleLoadTabInfo = async (tabTitle) => {
    if (tabTitle === 'articles' && articleData === null) {
      try {
        const response = await axios.post(
          axiosPath('api_r_user_module_lawFinder_getArticleData'),
          { lawId: law.id, moduleBridgeHex },
        );
        setArticleData(response.data.articles);
        setCheckedArticles(response.data.checkedArticles);
        return response.data;
      } catch (err) {
        setArticleData('error');
      }
    }
  };

  const handleCheckArticle = async (articleID) => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_lawFinder_checkArticle'),
        { articleID, moduleBridgeHex },
      );
      setCheckedArticles(response.data);
      return response.data;
    } catch (err) {}
  };

  const columns = [
    {
      name: <Translator file="user" name="user.obligation.evidence.date" />,
      selector: (row) => row?.dateStamp ?? '',
      sortable: true,
      width: '120px',
    },
    {
      name: <Translator file="module-lawFinder" name="module.lf.law-files" />,
      selector: (row) => row?.files ?? [],
      sortable: false,
      width: '90px',
      cell: (row) => (
        <div>
          {Array.isArray(row?.files) && row.files.length > 0 ? (
            <>
              <img
                src={`https://worldlex.net/assets/images/filetype_${
                  row.files[0]?.extension &&
                  row.files[0].extension in images.extensionIcons
                    ? images.extensionIcons[row.files[0].extension]
                    : 'file'
                }.png`}
                alt={row.files[0]?.name ?? 'file'}
              />
              {row.files.length > 1 && <span className="pl-1">...</span>}
            </>
          ) : (
            row?.files && '...'
          )}
        </div>
      ),
    },
    {
      name: <Translator file="module-legalCompliance" name="title" />,
      selector: (row) => row?.name ?? '',
      sortable: true,
    },
    {
      name: (
        <Translator
          file="module-legalCompliance"
          name="module.lc.evidence.description"
        />
      ),
      selector: (row) => row?.description ?? '',
      sortable: true,
    },
    {
      name: '',
      selector: (row) => row?.options ?? null,
      width: '24px',
      cell: (row) => (
        <div
          title={ReactDOMServer.renderToString(
            <Translator
              file="module-legalCompliance"
              name="module.lc.remove-evidence-confirm-button"
            />,
          )}
          onClick={() => setShowDeleteEvidenceModal(row.id)}
          style={{ cursor: 'pointer' }}
        >
          <AppRemixIcon icon="delete-bin-6" color={colors.danger} />
        </div>
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  const getLawFile = useCallback(
    async (lf) =>
      axios
        .post(axiosPath('api_r_user_module_lawFinder_lawFile_download'), {
          moduleBridgeHex,
          fileId: lf.id,
          type: lf.type === 'modification' ? 'lawModificationFile' : 'lawFile',
        })
        .then(({ data }) => (window.location = data))
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex],
  );

  const getLawEvidence = useCallback(
    async (url) =>
      axios
        .post(axiosPath('api_r_user_module_lawFinder_lawEvidence_download'), {
          moduleBridgeHex,
          lawID: law.id,
          evidence: url,
        })
        .then(({ data }) => (window.open("", "_blank", "noopener,noreferrer").location = data))
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, law],
  );

  const ExpandedRow = ({ data }) => (
    <ExpandedRowContainer className="border border-light border-top-0 p-2">
      XXXXXXXXXXXXXXXXXXX
      <div>
        <h3>
          <Translator file="module-legalCompliance" name="title" />
        </h3>
        <div>{data.name}</div>
        <h3>
          <Translator
            file="module-legalCompliance"
            name="module.lc.evidence.description"
          />
        </h3>
        <div>{data.description}</div>
      </div>
      <div>
        <h3>
          <Translator file="module-lawFinder" name="module.lf.law-files" />
        </h3>
        <table className="table table-hover">
          <tbody>
            {data.files &&
              data.files.map((f) => (
                <FileRow key={f.id}>
                  <td className="col-lg-1 text-center">
                    <span onClick={() => getLawEvidence(f)} className="pointer">
                      <img
                        src={`https://worldlex.net/assets/images/filetype_${
                          f.extension in images.extensionIcons
                            ? images.extensionIcons[f.extension]
                            : 'file'
                        }.png`}
                        alt={f.name}
                      />
                    </span>
                    {/* 23-01-2026 */}
                    {/* <a
                      className="pointer"
                      href={`${global.urlData.userApiUrl}m/law-finder/get-law-evidence/${moduleBridgeHex}/${law.id}/${data.id}/${f.id}/`}
                    >
                      <img
                        src={`https://worldlex.net/assets/images/filetype_${
                          f.extension in images.extensionIcons
                            ? images.extensionIcons[f.extension]
                            : "file"
                        }.png`}
                        alt={f.name}
                      />
                    </a> */}
                  </td>
                  <td className="col-lg-11">{f.name}</td>
                </FileRow>
              ))}
          </tbody>
        </table>
      </div>
    </ExpandedRowContainer>
  );

  return (
    <div>
      <Container evaluation={evaluation}>
        {evaluation ? (
          <>
            {!lawDetail || evaluationIsLoading ? (
              <div className="pointer" style={{ background: '#e8e8d5' }}>
                <AppSpinner>
                  <RemixIcon icon="loader-4" size={20} color={colors.primary} />
                </AppSpinner>
              </div>
            ) : (
              <div
                onClick={() => setShowChangeStateModal(true)}
                role="button"
                className="pointer"
                style={{
                  background: plantObligationStates[lawDetail.state].bgColor,
                }}
              >
                <RemixIcon
                  icon={plantObligationStates[lawDetail.state].icon}
                  size={20}
                  color={plantObligationStates[lawDetail.state].iconColor}
                />
              </div>
            )}

            <AppModal
              show={showChangeStateModal}
              isConfirmBtn={false}
              title={<Translator file="user" name="user.state" />}
              onClose={() => setShowChangeStateModal(false)}
            >
              {lawDetail && (
                <ul style={{ margin: 0, padding: 0 }}>
                  {evaluation.map((st) => (
                    <StateElement
                      key={st}
                      bg={plantObligationStates[st].bgColor}
                      icon={plantObligationStates[st].iconColor}
                      className={`pointer ${st === lawDetail.state && 'selected'}`}
                      onClick={() => handleChangeState(st)}
                    >
                      <RemixIcon icon="check" />
                      <Translator file="user" name={`user.obligations.state${st}`} />
                    </StateElement>
                  ))}
                </ul>
              )}
            </AppModal>
          </>
        ) : (
          <div />
        )}
        <div>{law.location}</div>
        <div>
          <div>
            {law.category} / {law.subcategory}
          </div>
          <p className="ellipsis"> {law.name}</p>
        </div>
        <div>
          <div>
            <span>
              <Translator
                file="module-lawFinder"
                name="module.lf.creationStamp-short"
              />
            </span>
            <span>{law.creationDateStamp || ''}</span>
          </div>
          <div>
            <span>
              <Translator
                file="module-lawFinder"
                name="module.lf.modificationStamp-short"
              />
            </span>
            <span>{law.modificationDateStamp || ''}</span>
          </div>
          {permissionsModule?.exportLaws && (
            <div>
              <AppRemixIcon
                icon="file-word"
                size="18"
                onClick={() => exportLawDocument(law.id)}
                title="Exportar"
                className="pointer"
                color={colors.primary}
              />
            </div>
          )}
        </div>
        <div>
          {!evidences
            ? law.lawFiles && (
                <>
                  {law.lawFiles.map((lf, i) => (
                    <Fragment key={i}>
                      {i < maxIcons && (
                        <span onClick={() => getLawFile(lf)} className="pointer">
                          <img
                            src="https://worldlex.net/assets/images/filetype_pdf.png"
                            alt={lf.name}
                          />
                        </span>
                        // 23-01-2026
                        // <a
                        //   key={lf.id}
                        //   href={`${
                        //     global.urlData.userApiUrl
                        //   }m/law-finder/get-law-file/${moduleBridgeHex}/${
                        //     lf.type === 'law'
                        //       ? 'lawFile'
                        //       : 'lawModificationFile'
                        //   }/${lf.id}/`}
                        // >
                        // <img
                        //   src="https://worldlex.net/assets/images/filetype_pdf.png"
                        //   alt={lf.name}
                        // />
                        //   <span>{lf.language.slug}</span>
                        // </a>
                      )}
                    </Fragment>
                  ))}
                  {law.externalWebLink.length > 0 &&
                    law.externalWebLink.map(
                      (ewl) =>
                        ewl.isLawConsolidation && (
                          <div
                            className="pointer"
                            onClick={() => window.open(ewl.url)}
                            title={ReactDOMServer.renderToString(
                              <Translator file="admin" name="general.web-link" />,
                            )}
                          >
                            <AppRemixIcon
                              icon="link"
                              size={16}
                              color={colors.primary}
                              noVariants
                            />
                          </div>
                        ),
                    )}
                  {law.lawFiles.length > maxIcons && (
                    <div>
                      <RemixIcon icon="more" size={16} color={colors.primary} />
                      <span>Más</span>
                    </div>
                  )}
                </>
              )
            : lawDetail && (
                <>
                  {lawDetail.evidences.map(
                    (e) =>
                      e.files &&
                      e.files.map((f, i) => {
                        if (i < 9) {
                          return (
                            <span
                              onClick={() => getLawEvidence(f)}
                              className="pointer"
                            >
                              <img
                                src={`https://worldlex.net/assets/images/filetype_${
                                  f.extension in images.extensionIcons
                                    ? images.extensionIcons[f.extension]
                                    : 'file'
                                }.png`}
                                alt={f.name}
                              />
                            </span>
                            // 23-01-2026
                            // <span
                            //   key={f.id}
                            //   className="pointer"
                            //   onClick={() => getLawEvidence(e, f)}
                            //   href={`${global.urlData.userApiUrl}m/law-finder/get-law-evidence/${moduleBridgeHex}/${law.id}/${e.id}/${f.id}/`}
                            // >
                            // <img
                            //   src={`https://worldlex.net/assets/images/filetype_${
                            //     f.extension in images.extensionIcons
                            //       ? images.extensionIcons[f.extension]
                            //       : 'file'
                            //   }.png`}
                            //   alt={f.name}
                            // />
                            // </span>
                          );
                        }
                      }),
                  )}
                  {lawDetail.evidences.length >= 9 ||
                    (law.externalWebLink.length > 0 && (
                      <div>
                        <RemixIcon icon="more" size={16} color={colors.primary} />
                        <span>Más</span>
                      </div>
                    ))}
                </>
              )}
        </div>
        <div onClick={() => setIsOpen(!isOpen)}>
          {!isOpen ? <RemixIcon icon="add" /> : <RemixIcon icon="subtract" />}
        </div>
      </Container>
      <ExpandBlock isOpen={isOpen}>
        <div className="p-2">
          <Tabs
            defaultActiveKey="info"
            onSelect={(tabTitle) => handleLoadTabInfo(tabTitle)}
          >
            <Tab
              eventKey="info"
              title={
                <Translator file="module-lawFinder" name="module.lf.information" />
              }
              className="p-3 border border-top-0 bg-white"
              style={{ height: '300px' }}
            >
              <TabContainer>
                <small className="float-right">
                  ID:
                  {law.id}
                </small>
                {permissions?.lawFinder?.filterLawYear && law.lawYear && (
                  <>
                    <br />
                    <small className="float-right">
                      Año de ley:
                      {law.lawYear}
                    </small>
                  </>
                )}
                {permissions?.lawFinder?.filterLawNumber && law.lawNumber && (
                  <>
                    <br />
                    <small className="float-right">
                      Número de Ley:
                      {law.lawNumber}
                    </small>
                  </>
                )}
                <CategoryBlock className="breadcrumb">
                  <li className="breadcrumb-item">{law.category}</li>
                  <li className="breadcrumb-item">{law.subcategory}</li>
                </CategoryBlock>
                <div>
                  <LawTitle>
                    <Translator file="module-lawFinder" name="module.lf.law-name" />
                  </LawTitle>
                  <LawName>{law.name}</LawName>
                  {law.modification && (
                    <>
                      <hr />
                      <LawName
                        dangerouslySetInnerHTML={{ __html: law.modification }}
                      />
                    </>
                  )}
                </div>
              </TabContainer>
            </Tab>
            <Tab
              eventKey="files"
              title={
                <Translator file="module-lawFinder" name="module.lf.law-files" />
              }
              className="p-3 border border-top-0 bg-white"
              style={{ height: '300px' }}
            >
              <TabContainer>
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th className="col-lg-1 text-center">
                        <Translator file="user" name="user.download" />
                      </th>
                      <th className="col-lg-7">
                        <Translator file="general" name="general.name" />
                      </th>
                      <th className="col-lg-2">
                        <Translator
                          file="module-microsite"
                          name="module.microsite.article-type"
                        />
                      </th>
                      <th className="col-lg-2">
                        <Translator file="user" name="user.language" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {law.externalWebLink.map((ewl) => (
                      <FileRow>
                        <td className="text-center">
                          <div
                            className="pointer"
                            onClick={() => window.open(ewl.url)}
                          >
                            <AppRemixIcon
                              icon="link"
                              size={14}
                              color={colors.primary}
                              noVariants
                            />
                          </div>
                        </td>
                        <td>{ewl.name}</td>
                        <td>{ewl.type}</td>
                        <td></td>
                      </FileRow>
                    ))}
                    {law.lawFiles.map((lf) => (
                      <FileRow key={lf.id}>
                        <td className="text-center">
                          <span onClick={() => getLawFile(lf)} className="pointer">
                            <img
                              src="https://worldlex.net/assets/images/filetype_pdf.png"
                              alt={lf.name}
                            />
                          </span>
                        </td>
                        <td>{lf.name}</td>
                        <td>
                          {lf.type === 'law' ? (
                            <Translator
                              file="module-lawFinder"
                              name="module.lf.law-name"
                            />
                          ) : (
                            <Translator
                              file="module-lawFinder"
                              name="module.lf.law-modification"
                            />
                          )}
                        </td>
                        <td>{lf.language.name}</td>
                      </FileRow>
                    ))}
                  </tbody>
                </table>
              </TabContainer>
            </Tab>
            {articleObligations && (
              <Tab
                eventKey="articles"
                title={
                  <Translator file="module-lawFinder" name="module.lf.articles" />
                }
                className="p-3 border border-top-0 bg-white"
                style={{ height: '300px' }}
              >
                <TabContainer>
                  {articleData && checkedArticles && articleData !== 'error' ? (
                    articleData.length > 0 ? (
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th className="col-lg-1">Check</th>
                            <th className="col-lg-6">
                              <Translator file="law" name="law.article.name" />
                            </th>
                            <th className="col-lg-5">
                              <Translator file="evaluation" name="obligation.title" />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {articleData.map((a) => (
                            <FileRow key={a.id}>
                              <td
                                className="pointer"
                                onClick={() => handleCheckArticle(a.id)}
                              >
                                <AppRemixIcon
                                  icon={
                                    checkedArticles.includes(a.id)
                                      ? 'checkbox'
                                      : 'checkbox-blank'
                                  }
                                  size={14}
                                />
                              </td>
                              <td className="pointer">
                                <InfoModal
                                  title={
                                    <Translator
                                      file="module-lawFinder"
                                      name="module.lf.information"
                                    />
                                  }
                                  selector={
                                    <span className="text-primary text-decoration-underline">
                                      {a.name.length > 100
                                        ? `${a.name.slice(0, 100)}...`
                                        : a.name}
                                    </span>
                                  }
                                >
                                  <div
                                    style={{
                                      maxHeight: '400px',
                                      overflow: 'auto',
                                    }}
                                  >
                                    <h6>{a.name}</h6>

                                    <div
                                      style={{
                                        whiteSpace: 'pre-line',
                                      }}
                                    >
                                      {a.modification}
                                    </div>
                                  </div>
                                </InfoModal>
                              </td>
                              <td className="pointer">
                                <InfoModal
                                  title={
                                    <Translator
                                      file="module-lawFinder"
                                      name="module.lf.information"
                                    />
                                  }
                                  selector={
                                    <span className="text-primary text-decoration-underline">
                                      {a.obligations.length}{' '}
                                      <Translator
                                        file="module-lawFinder"
                                        name="module.lf-obligation-s"
                                      />
                                    </span>
                                  }
                                  size="lg"
                                >
                                  <div
                                    style={{
                                      maxHeight: '400px',
                                      overflow: 'auto',
                                    }}
                                  >
                                    {a.obligations.map((o) => (
                                      <div
                                        key={o.id}
                                        style={{
                                          borderBottom: '1px solid grey',
                                        }}
                                        className="p-3"
                                      >
                                        <h6>
                                          ID:
                                          {o.id}
                                        </h6>
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html: o.obligation,
                                          }}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </InfoModal>
                              </td>
                            </FileRow>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="alert alert-info">
                        <Translator
                          file="module-lawFinder"
                          name="module.lf.no-articles"
                        />
                      </div>
                    )
                  ) : articleData === 'error' ? (
                    <div className="alert alert-danger">
                      <Translator file="general" name="error-500" />
                    </div>
                  ) : (
                    <NoInfo
                      text={<Translator file="general" name="loading" />}
                      icon={
                        <AppSpinner>
                          <RemixIcon icon="loader-4" size={24} color="#ccc" />
                        </AppSpinner>
                      }
                    />
                  )}
                </TabContainer>
              </Tab>
            )}
            {evidences && (
              <Tab
                eventKey="evidences"
                title={<Translator file="user" name="user.obligations.evidence" />}
                className="p-3 border border-top-0 bg-white"
                style={{ height: '300px' }}
              >
                <TabContainer>
                  {lawDetail?.evidences.length > 0 && (
                    <span
                      role="button"
                      className="btn btn-outline-primary"
                      onClick={() => setShowNewEvidenceModal(true)}
                    >
                      <Translator
                        file="module-legalCompliance"
                        name="module.lc.new-evidence"
                      />
                    </span>
                  )}
                  {lawDetail ? (
                    lawDetail.evidences.length > 0 ? (
                      <DataTable
                        columns={columns}
                        data={lawDetail.evidences}
                        striped
                        responsive
                        noHeader
                        noContextMenu
                        expandableRows
                        expandOnRowClicked
                        expandableRowsComponent={ExpandedRow}
                      />
                    ) : (
                      <div style={{ textAlign: 'center' }}>
                        <Translator
                          file="module-legalCompliance"
                          name="no.attached.evidences"
                        />
                        <br />
                        <span
                          className="btn btn-primary"
                          onClick={() => setShowNewEvidenceModal(true)}
                        >
                          <Translator
                            file="user"
                            name="user.obligation.evidence.new"
                          />
                        </span>
                      </div>
                    )
                  ) : (
                    <NoInfo
                      text={<Translator file="general" name="loading" />}
                      icon={
                        <AppSpinner>
                          <RemixIcon icon="loader-4" size={24} color="#ccc" />
                        </AppSpinner>
                      }
                    />
                  )}
                </TabContainer>
                <AppModal
                  show={showNewEvidenceModal}
                  title={
                    <Translator file="user" name="user.obligation.evidence.new" />
                  }
                  onClose={() => setShowNewEvidenceModal(false)}
                >
                  <Formik
                    validateOnChange={false}
                    validateOnBlur={false}
                    enableReinitialize
                    initialValues={{
                      name: '',
                      description: '',
                      files: null,
                    }}
                    onSubmit={(v) => handleAddEvidence(v)}
                  >
                    {({ setFieldValue, resetForm, handleSubmit, values, errors }) => (
                      <>
                        <section className="content mb-3">
                          <div className="row">
                            <div className="col-12">
                              <AppInput
                                title={
                                  <Translator
                                    file="module-legalCompliance"
                                    name="title"
                                  />
                                }
                                placeholder={
                                  <Translator
                                    file="module-lawFinder"
                                    name="module.lf.law-text"
                                  />
                                }
                                icon={<RemixIcon icon="font-color" noVariants />}
                                onChange={(v) => setFieldValue('name', v)}
                                value={values.name}
                                isClearable
                              />
                              <AppTextArea
                                title={
                                  <Translator
                                    file="module-legalCompliance"
                                    name="module.lc.evidence.description"
                                  />
                                }
                                icon={<RemixIcon icon="font-color" noVariants />}
                                onChange={(v) => setFieldValue('description', v)}
                                value={values.description}
                                isClearable
                              />
                              <AppFileUploader
                                isMulti
                                placeholder={
                                  !values.files || values.files.length === 0
                                    ? 'Selecciona uno o varios archivos'
                                    : `${values.files.length} archivos seleccionados`
                                }
                                onChange={(opt) => setFieldValue('files', opt)}
                                btnText={
                                  <Translator file="general" name="search..." />
                                }
                              />
                              <span
                                role="button"
                                className="btn btn-primary btn-block mt-2"
                                onClick={
                                  !evaluationIsLoading ? handleSubmit : undefined
                                }
                              >
                                {evaluationIsLoading ? (
                                  <AppSpinner>
                                    <RemixIcon
                                      icon="loader-4"
                                      size={16}
                                      color="white"
                                    />
                                  </AppSpinner>
                                ) : (
                                  <Translator
                                    file="module-correctiveActions"
                                    name="module.ca.add"
                                  />
                                )}
                              </span>
                            </div>
                          </div>
                        </section>
                      </>
                    )}
                  </Formik>
                </AppModal>
              </Tab>
            )}
          </Tabs>
        </div>
      </ExpandBlock>
      <AppModal
        show={showDeleteEvidenceModal !== null}
        title={
          <Translator
            file="module-legalCompliance"
            name="module.lc.remove-evidence-confirm-button"
          />
        }
        onClose={() => setShowDeleteEvidenceModal(null)}
      >
        <Translator
          file="module-legalCompliance"
          name="module.lc.remove-evidence-confirm"
          isHtml
        />
        <span
          className={`btn btn-block btn-danger ${
            removeEvidenceIsLoading && 'disabled'
          }`}
          onClick={() =>
            !removeEvidenceIsLoading ? handleDeleteEvidence() : undefined
          }
        >
          {!removeEvidenceIsLoading ? (
            <Translator
              file="module-legalCompliance"
              name="module.lc.remove-evidence-confirm-button"
            />
          ) : (
            <AppSpinner>
              <RemixIcon icon="loader-4" size={16} color="white" />
            </AppSpinner>
          )}
        </span>
      </AppModal>
    </div>
  );
};

export default SingleLaw;

const Container = styled.div`
  width: 100%;
  height: 90px;
  background: white;
  margin-top: 10px;
  border: 1px solid ${colors.primaryLight};
  border-radius: 2px;
  display: grid;
  grid-template-columns: ${(props) =>
      props.evaluation !== false ? '30px' : '10px'} 140px 1fr 200px 150px 30px;
  overflow: hidden;
  padding: 0;
  & > div {
    text-align: center;
    font-size: 12px;
    padding: 0 6px;
    max-height: 90px;
    &:nth-child(1) {
      display: flex;
      align-items: center;
      justify-content: center;
      ${(props) =>
        props.evaluation === false ? `background: ${colors.primaryLight};` : ''}
      color: white;
    }
    &:nth-child(2) {
      display: flex;
      align-items: center;
    }
    &:nth-child(3) {
      display: flex;
      flex-direction: column;
      align-items: start;
      border-left: 1px solid ${colors.primaryLight};
      div {
        padding-top: 4px;
        color: ${colors.primaryLight};
        font-weight: bold;
      }
      p {
        text-align: justify;
        height: 57px;
        overflow: hidden;
        margin: 0;
      }
    }
    &:nth-child(4) {
      display: flex;
      align-items: baseline;
      justify-content: center;
      flex-direction: column;
      border-left: 1px solid ${colors.primaryLight};
      div {
        display: block;
        margin: 4px 0;
      }
      div > span:nth-child(1) {
        display: inline-block;
        color: ${colors.primaryLight};
        font-size: 10px;
        width: 80px;
        text-align: left;
      }
      div > span:nth-child(2) {
        color: ${colors.primary};
        font-size: 12px;
      }
    }
    &:nth-child(5) {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr 1fr;
      padding-top: 8px;
      border-left: 1px solid ${colors.primaryLight};
      a,
      div {
        display: flex;
        flex-direction: column;
        align-items: center;
        &:hover {
          text-decoration: none;
        }
      }
    }
    &:nth-child(6) {
      display: flex;
      align-items: center;
      justify-content: center;
      background: ${colors.primary};
      color: white;
      font-size: 20px;
    }
  }
`;
const StateElement = styled.li`
  list-style: none;
  padding: 4px;
  margin: 4px;
  border: 1px solid white;
  i {
    background: ${(props) => props.bg};
    color: ${(props) => props.icon};
    padding: 4px;
    margin-right: 6px;
    border-radius: 2px;
  }
  &:hover {
    border: 1px solid ${colors.grey400};
  }
  &.selected {
    background: ${colors.grey400};
  }
`;

const ExpandBlock = styled.div`
  overflow: hidden;
  height: ${(props) => (props.isOpen ? 360 : 0)}px;
  transition: height ease 0.5s;
  margin: 0 6px;
  border: 1px solid ${colors.primaryLight};
  border-bottom: ${(props) => (props.isOpen ? 1 : 0)}px solid
    ${colors.primaryLight};
  border-top: 0;
  border-bottom-right-radius: 2px;
  border-bottom-left-radius: 2px;
  background: white;
`;
const TabContainer = styled.div`
  overflow-y: auto;
  height: 280px;
`;
const CategoryBlock = styled.ol`
  color: ${colors.primaryLight};
  font-size: 12px;
`;
const LawTitle = styled.h1`
  font-size: 14px;
`;
const LawName = styled.p`
  font-size: 12px;
  h3 {
    font-size: 14px;
  }
  strong {
    color: ${colors.primary};
  }
`;

const FileRow = styled.tr`
  td {
    font-size: 11px;
    padding: 2px;
  }
`;
const ExpandedRowContainer = styled.div`
  display: grid;
  grid-template-columns: 0.6fr 0.4fr;
  h3 {
    font-size: 14px;
    margin: 0;
  }
  &:nth-child(1) {
    div {
      margin-bottom: 8px;
    }
  }
`;
